<template>
  <div class="min-h-screen bg-theme-bg pb-10">

    <!-- Sticky header with progress -->
    <div class="sticky top-0 z-20 bg-theme-card border-b border-theme-border">
      <div class="max-w-2xl mx-auto px-4 pt-4 pb-3">
        <div class="mb-3">
          <h1 class="text-base font-bold text-theme-text">List an Item</h1>
          <p class="text-xs text-theme-muted mt-0.5">Step {{ currentStep }} of {{ totalSteps }} — {{ stepLabels[currentStep - 1] }}</p>
        </div>

        <!-- Step indicator -->
        <div class="flex items-center">
          <template v-for="(label, i) in stepLabels" :key="i">
            <button
              type="button"
              @click="i + 1 < currentStep ? currentStep = i + 1 : null"
              class="flex flex-col items-center gap-1 focus:outline-none"
              :class="i + 1 < currentStep ? 'cursor-pointer' : 'cursor-default'"
            >
              <div
                class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200"
                :class="currentStep > i + 1
                  ? 'bg-primary-600 text-white'
                  : currentStep === i + 1
                    ? 'bg-primary-600 text-white ring-4 ring-primary-600/20'
                    : 'bg-theme-bg2 text-theme-muted border border-theme-border'"
              >
                <i v-if="currentStep > i + 1" class="pi pi-check text-[9px]"></i>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="text-[9px] font-medium hidden sm:block"
                :class="currentStep === i + 1 ? 'text-primary-600' : 'text-theme-muted'">
                {{ label }}
              </span>
            </button>
            <div
              v-if="i < stepLabels.length - 1"
              class="flex-1 h-0.5 mb-3 sm:mb-4 mx-1 transition-all duration-300"
              :class="currentStep > i + 1 ? 'bg-primary-600' : 'bg-theme-border'"
            ></div>
          </template>
        </div>
      </div>
    </div>

    <form @submit.prevent="handleSubmit" class="max-w-2xl mx-auto px-4 py-6">

      <!-- ── Step 1: Basics ── -->
      <div v-show="currentStep === 1" class="space-y-5">
        <div>
          <h2 class="text-lg font-semibold text-theme-text">Item Basics</h2>
          <p class="text-sm text-theme-muted mt-0.5">The core details about what you're renting out.</p>
        </div>

        <!-- Item Name -->
        <div>
          <label class="block text-sm font-medium text-theme-text2 mb-1.5">Item Name</label>
          <input
            type="text"
            placeholder="e.g. Canon EOS R5 Camera"
            v-model="itemName"
            @input="validateItemName"
            :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
              itemNameError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']"
          />
          <p v-if="itemNameError" class="text-red-500 text-xs mt-1">{{ itemNameError }}</p>
        </div>

        <!-- Category -->
        <div>
          <label class="block text-sm font-medium text-theme-text2 mb-1.5">Category</label>
          <select
            v-model="selectedCategory"
            @change="validateCategory"
            :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
              categoryError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']"
          >
            <option value="" disabled>Select a category</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </select>
          <p v-if="categoryError" class="text-red-500 text-xs mt-1">{{ categoryError }}</p>
        </div>

        <!-- Daily Rate + Quantity -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">Daily Rate</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              v-model.number="hourlyRate"
              @input="validateHourlyRate"
              :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                hourlyRateError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']"
            />
            <p v-if="hourlyRateError" class="text-red-500 text-xs mt-1">{{ hourlyRateError }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">Available Qty</label>
            <input
              type="number"
              placeholder="1"
              v-model.number="availableItems"
              @input="validateAvailableItems"
              :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                availableItemsError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']"
            />
            <p v-if="availableItemsError" class="text-red-500 text-xs mt-1">{{ availableItemsError }}</p>
          </div>
        </div>
      </div>

      <!-- ── Step 2: Details ── -->
      <div v-show="currentStep === 2" class="space-y-5">
        <div>
          <h2 class="text-lg font-semibold text-theme-text">Item Details</h2>
          <p class="text-sm text-theme-muted mt-0.5">Describe your item and set any rental terms.</p>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-theme-text2 mb-1.5">Description</label>
          <div
            ref="descriptionEditorRef"
            class="quill-editor-wrap rounded-xl border bg-theme-input text-theme-input-text"
            :class="descriptionError ? 'border-red-500' : 'border-theme-input-border'"
          ></div>
          <p v-if="descriptionError" class="text-red-500 text-xs mt-1">{{ descriptionError }}</p>
        </div>

        <!-- Tags -->
        <div>
          <label class="block text-sm font-medium text-theme-text2 mb-1.5">Tags</label>
          <input
            type="text"
            v-model="tagsInput"
            placeholder="Type a tag and press Enter"
            @keydown.enter.prevent="addTags"
            class="block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border-theme-input-border focus:border-primary-500"
          />
          <p class="text-xs text-theme-muted mt-1">Press Enter to add each tag</p>
          <div v-if="tags.length" class="flex flex-wrap gap-1.5 mt-2">
            <span
              v-for="(tag, index) in tags"
              :key="index"
              class="inline-flex items-center gap-1 bg-primary-600/10 text-primary-600 border border-primary-600/20 rounded-lg px-2.5 py-1 text-xs font-medium"
            >
              {{ tag }}
              <button type="button" @click="removeTag(index)" class="hover:text-red-500 leading-none ml-0.5">×</button>
            </span>
          </div>
        </div>

        <!-- Rental Terms -->
        <div>
          <label class="block text-sm font-medium text-theme-text2 mb-1.5">Rental Terms</label>
          <div
            ref="termsEditorRef"
            class="quill-editor-wrap rounded-xl border bg-theme-input text-theme-input-text"
            :class="termsError ? 'border-red-500' : 'border-theme-input-border'"
          ></div>
          <p v-if="termsError" class="text-red-500 text-xs mt-1">{{ termsError }}</p>
        </div>
      </div>

      <!-- ── Step 3: Photos ── -->
      <div v-show="currentStep === 3" class="space-y-5">
        <div>
          <h2 class="text-lg font-semibold text-theme-text">Item Photos</h2>
          <p class="text-sm text-theme-muted mt-0.5">Upload up to 4 clear photos of your item.</p>
        </div>

        <!-- Dropzone -->
        <div
          @drop.prevent="handleDrop"
          @dragover.prevent
          @click="triggerFileSelect"
          :class="['border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors',
            imageError ? 'border-red-400 bg-red-500/5' : 'border-theme-input-border hover:border-primary-500 hover:bg-primary-600/5 bg-theme-input']"
        >
          <div class="w-14 h-14 rounded-2xl bg-primary-600/10 flex items-center justify-center mx-auto mb-4">
            <i class="pi pi-image text-primary-600 text-2xl"></i>
          </div>
          <p class="text-sm font-semibold text-theme-text">Drop images here, or click to browse</p>
          <p class="text-xs text-theme-muted mt-1">Max 4 images · JPG, PNG, WEBP</p>
        </div>
        <input type="file" id="images" ref="fileInput" multiple @change="handleFileSelect" hidden accept="image/*" />

        <!-- Previews -->
        <div v-if="imagePreviews.length" class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div
            v-for="(src, i) in imagePreviews"
            :key="i"
            class="relative aspect-square rounded-xl overflow-hidden border border-theme-border bg-theme-bg2"
          >
            <img :src="src" alt="Preview" class="w-full h-full object-cover" />
            <button
              type="button"
              @click.stop="imagePreviews.splice(i, 1); images.splice(i, 1)"
              class="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600 transition text-xs font-bold"
            >×</button>
          </div>
        </div>

        <p v-if="imageError" class="text-red-500 text-xs">{{ imageError }}</p>
      </div>

      <!-- ── Step 4: Location ── -->
      <div v-show="currentStep === 4" class="space-y-5">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-theme-text">Item Location</h2>
            <p class="text-sm text-theme-muted mt-0.5">Where is the item available for pickup?</p>
          </div>
          <button
            type="button"
            @click="showMapPicker = true"
            class="flex-shrink-0 flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 border border-primary-300 hover:border-primary-500 rounded-xl px-3 py-2 transition bg-primary-600/5 hover:bg-primary-600/10"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Pick on map
          </button>
        </div>

        <!-- Street Address -->
        <div class="relative">
          <label class="block text-sm font-medium text-theme-text2 mb-1.5">Street Address</label>
          <input
            type="text"
            id="autocomplete"
            ref="autocompleteInput"
            v-model="streetAddress"
            @input="validateStreetAddress(); onAddressInput()"
            @blur="setTimeout(() => clearAddrSuggestions(), 200)"
            autocomplete="off"
            placeholder="Start typing your address"
            :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
              streetAddressError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']"
          />
          <ul v-if="showAddrSuggestions && addrSuggestions.length"
            class="absolute z-50 left-0 right-0 mt-1 bg-theme-card border border-theme-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
            <li v-for="s in addrSuggestions" :key="s.placeId"
              class="px-3 py-2.5 hover:bg-theme-bg cursor-pointer text-sm"
              @mousedown.prevent="selectAddrSuggestion(s)">
              <span class="font-medium text-theme-text">{{ s.main }}</span>
              <span v-if="s.secondary" class="text-theme-muted ml-1">{{ s.secondary }}</span>
            </li>
          </ul>
          <p v-if="streetAddressError" class="text-red-500 text-xs mt-1">{{ streetAddressError }}</p>
        </div>

        <!-- City + State -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">City</label>
            <input type="text" v-model="city" @input="validateCity" placeholder="City"
              :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                cityError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']" />
            <p v-if="cityError" class="text-red-500 text-xs mt-1">{{ cityError }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">State / Region</label>
            <input type="text" v-model="state" @input="validateState" placeholder="State"
              :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                stateError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']" />
            <p v-if="stateError" class="text-red-500 text-xs mt-1">{{ stateError }}</p>
          </div>
        </div>

        <!-- Zip + Country -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">Zip / Postal Code</label>
            <input type="text" v-model="zipCode" @input="validateZipCode" placeholder="00100"
              :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                zipCodeError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']" />
            <p v-if="zipCodeError" class="text-red-500 text-xs mt-1">{{ zipCodeError }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-theme-text2 mb-1.5">Country</label>
            <input type="text" v-model="country" @input="validateCountry" placeholder="Kenya"
              :class="['block w-full border rounded-xl bg-theme-input text-theme-input-text px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                countryError ? 'border-red-500' : 'border-theme-input-border focus:border-primary-500']" />
            <p v-if="countryError" class="text-red-500 text-xs mt-1">{{ countryError }}</p>
          </div>
        </div>
      </div>

      <!-- ── Step 5: Specifications ── -->
      <div v-show="currentStep === 5" class="space-y-5">
        <div>
          <h2 class="text-lg font-semibold text-theme-text">Specifications</h2>
          <p class="text-sm text-theme-muted mt-0.5">Add technical details about your item (optional).</p>
        </div>

        <div class="space-y-3">
          <div
            v-for="(spec, index) in specifications"
            :key="index"
            class="flex items-center gap-3 p-3 bg-theme-bg2 rounded-xl border border-theme-border"
          >
            <input
              type="text"
              v-model="spec.key"
              placeholder="e.g. Color"
              class="flex-1 min-w-0 border rounded-lg bg-theme-input text-theme-input-text px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border-theme-input-border"
            />
            <span class="text-theme-muted text-sm font-medium flex-shrink-0">:</span>
            <input
              type="text"
              v-model="spec.value"
              placeholder="e.g. Black"
              class="flex-1 min-w-0 border rounded-lg bg-theme-input text-theme-input-text px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border-theme-input-border"
            />
            <button
              v-if="specifications.length > 1"
              type="button"
              @click="removeSpecification(index)"
              class="w-7 h-7 flex items-center justify-center rounded-lg text-theme-muted hover:text-red-500 hover:bg-red-500/10 transition flex-shrink-0"
            >
              <i class="pi pi-trash text-xs"></i>
            </button>
          </div>
        </div>

        <button
          type="button"
          @click="addSpecification"
          class="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 px-3 py-2 border border-primary-600/30 hover:border-primary-600 rounded-xl transition bg-primary-600/5"
        >
          <i class="pi pi-plus text-xs"></i>
          Add specification
        </button>
      </div>

      <!-- ── Navigation ── -->
      <div class="flex items-center justify-between pt-8 mt-6 border-t border-theme-border">
        <button
          v-if="currentStep > 1"
          type="button"
          @click="goPrev"
          class="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-theme-border text-theme-text2 hover:bg-theme-bg2 text-sm font-medium transition"
        >
          <i class="pi pi-arrow-left text-xs"></i>
          Back
        </button>
        <div v-else></div>

        <button
          v-if="currentStep < totalSteps"
          type="button"
          @click="goNext"
          class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 text-white hover:bg-primary-700 text-sm font-semibold transition"
        >
          Continue
          <i class="pi pi-arrow-right text-xs"></i>
        </button>
        <button
          v-else
          type="submit"
          :disabled="loading"
          :class="['flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition',
            loading ? 'bg-primary-400 text-white cursor-not-allowed' : 'bg-primary-600 text-white hover:bg-primary-700']"
        >
          <i v-if="loading" class="pi pi-spinner pi-spin text-xs"></i>
          <i v-else class="pi pi-check text-xs"></i>
          {{ loading ? 'Submitting...' : 'Submit Listing' }}
        </button>
      </div>
    </form>

    <!-- Map Picker Modal -->
    <MapPickerModal
      :show="showMapPicker"
      @close="showMapPicker = false"
      @confirm="onMapConfirmEquip"
    />

    <!-- Role Switch Confirmation Modal -->
    <div
      v-if="showRoleConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
    >
      <div class="bg-theme-card border border-theme-border rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl">
        <h2 class="text-lg font-semibold text-theme-text mb-2">
          Switch to List mode?
        </h2>
        <p class="text-sm text-theme-text2 mb-4">
          You'll now be able to list your equipment for others to rent.
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="cancelRoleSwitch"
            class="px-4 py-2 text-sm rounded-lg border border-theme-border text-theme-text2 hover:bg-theme-bg2 focus:outline-none"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="confirmRoleSwitch"
            class="px-4 py-2 text-sm rounded-lg bg-secondary-500 text-theme-text font-semibold hover:brightness-110 focus:outline-none"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useAuthStore } from '@/store/auth'
import useNotifications from '@/store/notification'
import { useLocationStore } from '@/store/location'
import { storeToRefs } from 'pinia'
import { useLocationSearch } from '@/composables/useLocationSearch.js'
import { useTheme } from '@/composables/useTheme.js'
import MapPickerModal from '@/components/MapPickerModal.vue'
import darkLogo from '@/assets/images/dark_logo.png'
import { useLogger } from '@/composables/useLogger'

const logger = useLogger('CreateEquipment');
import lightLogo from '@/assets/images/logo.jpeg'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

const loading = ref(false)
const { isDark } = useTheme()
const authStore = useAuthStore()
const { showNotification } = useNotifications()
const api_base_url = import.meta.env.VITE_API_BASE_URL

// ---- LOCATION STORE (equipment) ----
const locationStore = useLocationStore()
const { equipmentAddress } = storeToRefs(locationStore)

// These computed wrappers keep the form fields in sync with the store
const streetAddress = computed({
  get: () => equipmentAddress.value.street,
  set: (val) => {
    equipmentAddress.value.street = val
  },
})

const city = computed({
  get: () => equipmentAddress.value.city,
  set: (val) => {
    equipmentAddress.value.city = val
  },
})

const state = computed({
  get: () => equipmentAddress.value.state,
  set: (val) => {
    equipmentAddress.value.state = val
  },
})

const zipCode = computed({
  get: () => equipmentAddress.value.zipCode,
  set: (val) => {
    equipmentAddress.value.zipCode = val
  },
})

const country = computed({
  get: () => equipmentAddress.value.country,
  set: (val) => {
    equipmentAddress.value.country = val
  },
})

// ---- OTHER FORM STATE ----
const itemName = ref('')
const hourlyRate = ref(null)
const selectedCategory = ref('')
const tagsInput = ref('')
const tags = ref([])
const description = ref('')
const terms = ref('')
const descriptionEditorRef = ref(null)
const termsEditorRef = ref(null)
let descriptionQuill = null
let termsQuill = null
const autocompleteInput = ref(null)
const showMapPicker = ref(false)
const onMapConfirmEquip = (result) => {
  equipmentAddress.value.street  = result.street_address || ''
  equipmentAddress.value.city    = result.city           || ''
  equipmentAddress.value.state   = result.state          || ''
  equipmentAddress.value.zipCode = result.zip_code       || ''
  equipmentAddress.value.country = result.country        || ''
  showMapPicker.value = false
}
const { suggestions: addrSuggestions, showSuggestions: showAddrSuggestions, fetchSuggestions: fetchAddrSuggestions, resolvePlace: resolveAddrPlace, clearSuggestions: clearAddrSuggestions } = useLocationSearch()
const countries = ref([])
const availableItems = ref(null)
const images = ref([])
const imagePreviews = ref([])

const specifications = ref([{ key: '', value: '' }])

const itemNameError = ref('')
const hourlyRateError = ref('')
const categoryError = ref('')
const tagsError = ref('')
const descriptionError = ref('')
const availableItemsError = ref('')
const imageError = ref('')
const streetAddressError = ref('')
const cityError = ref('')
const termsError = ref('')
const stateError = ref('')
const zipCodeError = ref('')
const countryError = ref('')

const router = useRouter()
const categories = ref([])

// ---- ROLE SWITCH MODAL ----
const showRoleConfirm = ref(false)
const pendingRoleSwitch = ref(false)

// ---- TAGS ----
const addTags = () => {
  const tag = tagsInput.value.trim()
  validateTags()

  if (tag) {
    tags.value.push(tag)
    tagsInput.value = ''
    tagsError.value = null
  } else {
    tagsError.value = 'Please enter a valid tag.'
  }
}

const removeTag = (index) => {
  tags.value.splice(index, 1)
}

// ---- SPECS ----
const addSpecification = () => {
  specifications.value.push({ key: '', value: '' })
}

const removeSpecification = (index) => {
  specifications.value.splice(index, 1)
}

// ---- COOKIES ----
const saveFormDataToCookies = () => {
  const formData = {
    itemName: itemName.value,
    hourlyRate: hourlyRate.value,
    selectedCategory: selectedCategory.value,
    tags: tags.value,
    description: description.value,
    terms: terms.value,
    streetAddress: streetAddress.value,
    city: city.value,
    state: state.value,
    zipCode: zipCode.value,
    country: country.value,
    availableItems: availableItems.value,
    specifications: specifications.value,
  }
  Cookies.set('itemFormData', JSON.stringify(formData), { expires: 7 })
}

const loadFormDataFromCookies = () => {
  const savedData = Cookies.get('itemFormData')
  if (savedData) {
    try {
      const formData = JSON.parse(savedData)
      itemName.value = formData.itemName || ''
      hourlyRate.value = formData.hourlyRate || null
      selectedCategory.value = formData.selectedCategory || ''
      tags.value = formData.tags || []
      description.value = formData.description || ''
      terms.value = formData.terms || ''
      streetAddress.value = formData.streetAddress || ''
      city.value = formData.city || ''
      state.value = formData.state || ''
      zipCode.value = formData.zipCode || ''
      country.value = formData.country || ''
      availableItems.value = formData.availableItems || null
      specifications.value = formData.specifications || [{ key: '', value: '' }]
    } catch (error) {
      logger.error('Error parsing form cookie data', error)
    }
  }
}

const clearFormCookies = () => {
  Cookies.remove('itemFormData')
}

// Watch & persist
watch(
  [
    itemName,
    hourlyRate,
    selectedCategory,
    tags,
    description,
    terms,
    streetAddress,
    city,
    state,
    zipCode,
    country,
    availableItems,
    specifications,
  ],
  () => {
    saveFormDataToCookies()
  },
  { deep: true }
)

onMounted(async () => {
  // RBAC: only lessors (and admins) may list items
  if (authStore.isAuthenticated) {
    const role = authStore.user?.role
    const isAdmin = !!authStore.user?.is_staff
    if (role !== 'lessor' && !isAdmin) {
      router.push({ name: 'home' })
      return
    }
  }

  // Load form data from cookies
  loadFormDataFromCookies()

  // Fetch categories
  try {
    const response = await axios.get(`${api_base_url}/api/categories/`)
    categories.value = response.data.results || response.data
  } catch (error) {
    logger.error('Error fetching categories', error)
  }

  // Initialize Quill editors
  const quillOptions = {
    theme: 'snow',
    placeholder: '',
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote'],
        ['link'],
        ['clean'],
      ],
    },
  }

  if (descriptionEditorRef.value) {
    descriptionQuill = new Quill(descriptionEditorRef.value, {
      ...quillOptions,
      placeholder: 'Describe your item...',
    })
    if (description.value) {
      descriptionQuill.root.innerHTML = description.value
    }
    descriptionQuill.on('text-change', () => {
      description.value = descriptionQuill.root.innerHTML
      validateDescription()
    })
  }

  if (termsEditorRef.value) {
    termsQuill = new Quill(termsEditorRef.value, {
      ...quillOptions,
      placeholder: 'Rental terms & conditions...',
    })
    if (terms.value) {
      termsQuill.root.innerHTML = terms.value
    }
    termsQuill.on('text-change', () => {
      terms.value = termsQuill.root.innerHTML
      validateTerms()
    })
  }
})

// ---- ADDRESS AUTOCOMPLETE ----
const onAddressInput = () => {
  fetchAddrSuggestions(streetAddress.value)
}

const selectAddrSuggestion = async (suggestion) => {
  clearAddrSuggestions()
  streetAddress.value = suggestion.full
  const result = await resolveAddrPlace(suggestion.placeId, suggestion._prediction)
  if (!result) return

  streetAddress.value = result.street_address || result.formatted || suggestion.full
  city.value = result.city || city.value
  state.value = result.state || state.value
  zipCode.value = result.zip_code || zipCode.value
  country.value = result.country || country.value

  if (result.lat != null && result.lng != null) {
    locationStore.setEquipmentCoords(result.lat, result.lng)
  }
}

// ---- HELPERS ----
const getUserIdFromToken = () => {
  const storedUser = Cookies.get('user')
  if (storedUser) {
    const userData = authStore.decryptData(storedUser)
    authStore.user.value = userData
    return userData.id
  }
  return null
}

// ---- VALIDATION ----
const validateItemName = () => {
  itemNameError.value = !itemName.value
    ? 'Item name is required.'
    : itemName.value.length < 3
    ? 'Item name must be at least 3 characters long.'
    : ''
}

const validateHourlyRate = () => {
  hourlyRateError.value =
    hourlyRate.value === null || hourlyRate.value <= 0
      ? 'Daily rate must be greater than 0.'
      : ''
}

const validateCategory = () => {
  categoryError.value = !selectedCategory.value ? 'Please select a valid category.' : ''
}

const validateTags = () => {
  tagsError.value = tagsInput.value.length < 3 ? 'Tags must be at least 3 characters long.' : ''
}

const validateDescription = () => {
  const text = descriptionQuill ? descriptionQuill.getText().trim() : description.value.trim()
  descriptionError.value = !text
    ? 'Description is required.'
    : text.length < 10
    ? 'Description must be at least 10 characters long.'
    : ''
}

const validateTerms = () => {
  const text = termsQuill ? termsQuill.getText().trim() : terms.value.trim()
  termsError.value = !text
    ? 'Terms are required.'
    : text.length < 10
    ? 'Terms must be at least 10 characters long.'
    : ''
}

const validateAvailableItems = () => {
  availableItemsError.value =
    availableItems.value <= 0 ? 'Please enter a valid number of available items.' : ''
}

const validateStreetAddress = () => {
  streetAddressError.value = !streetAddress.value ? 'Street address is required.' : ''
}

const validateCity = () => {
  cityError.value = !city.value ? 'City is required.' : ''
}

const validateState = () => {
  stateError.value = !state.value ? 'State is required.' : ''
}

const validateZipCode = () => {
  zipCodeError.value = !zipCode.value ? 'Zip code is required.' : ''
}

const validateCountry = () => {
  countryError.value = !country.value ? 'Country is required.' : ''
}

const validateImages = () => {
  imageError.value =
    images.value.length === 0
      ? 'Please upload at least one image.'
      : images.value.length > 4
      ? 'You can only upload a maximum of four images.'
      : ''
}

const isFormInvalid = computed(() => {
  return (
    itemNameError.value ||
    hourlyRateError.value ||
    categoryError.value ||
    tagsError.value ||
    descriptionError.value ||
    availableItemsError.value ||
    imageError.value ||
    streetAddressError.value ||
    cityError.value ||
    stateError.value ||
    zipCodeError.value ||
    countryError.value
  )
})

// ---- FILE HANDLING ----
const generateImagePreviews = (files) => {
  imagePreviews.value = files.map((file) => URL.createObjectURL(file))
}

const handleFileSelect = (event) => {
  images.value = Array.from(event.target.files)
  generateImagePreviews(images.value)
  validateImages()
}

const handleDrop = (event) => {
  images.value = Array.from(event.dataTransfer.files)
  generateImagePreviews(images.value)
  validateImages()
}

const triggerFileSelect = () => {
  document.getElementById('images').click()
}

// ---- SUBMIT ----
const handleSubmit = async () => {
  // Check if not authenticated
  if (!authStore.isAuthenticated) {
    showNotification('Login Required', 'Please log in to list equipment.', 'warning')
    authStore.redirectTo = '/list-item'
    router.push({ name: 'login' })
    return
  }
  
  // Check if authenticated but not a lessor
  const _submitRole = authStore.user?.role
  const _submitIsAdmin = !!authStore.user?.is_staff
  if (_submitRole !== 'lessor' && !_submitIsAdmin) {
    pendingRoleSwitch.value = true
    showRoleConfirm.value = true
    return
  }

  validateItemName()
  validateHourlyRate()
  validateCategory()
  validateDescription()
  validateTerms()
  validateAvailableItems()
  validateStreetAddress()
  validateCity()
  validateState()
  validateZipCode()
  validateCountry()
  validateImages()

  const hasErrors =
    itemNameError.value ||
    hourlyRateError.value ||
    categoryError.value ||
    tagsError.value ||
    descriptionError.value ||
    termsError.value ||
    availableItemsError.value ||
    streetAddressError.value ||
    cityError.value ||
    stateError.value ||
    zipCodeError.value ||
    countryError.value ||
    imageError.value

  if (hasErrors) return

  // Ensure we have coordinates for this item based on address text
  await locationStore.resolveEquipmentCoordsFromAddress()
  // If you want to debug:
  // console.log('Equipment coords:', locationStore.equipmentCoords)

  const formData = new FormData()
  const userId = getUserIdFromToken()

  formData.append('owner', userId)
  formData.append('name', itemName.value)
  formData.append('description', description.value)
  formData.append('hourly_rate', parseFloat(hourlyRate.value))
  formData.append('is_available', true)
  formData.append('category', selectedCategory.value)
  formData.append('available_quantity', availableItems.value)
  formData.append('terms', terms.value)
  formData.append('specifications', JSON.stringify(specifications.value))
  formData.append('street_address', streetAddress.value)
  formData.append('city', city.value)
  formData.append('state', state.value)
  formData.append('zip_code', zipCode.value)
  formData.append('country', country.value)

  const tagsArray = tags.value
  formData.append('tags', JSON.stringify(tagsArray))

  images.value.forEach((image) => {
    formData.append('images', image)
  })

  // Only add lat/lng to backend if/when your API expects it:
  // if (locationStore.equipmentCoords) {
  //   formData.append('latitude', locationStore.equipmentCoords.lat)
  //   formData.append('longitude', locationStore.equipmentCoords.lng)
  // }

  const user = authStore?.user || {}
  if (user?.role === 'lessor' && !user?.can_list_items) {
    showNotification(
      'Documents not yet verified',
      'Your identity documents must be verified by our team before you can list items.',
      'info'
    )
    try {
      await router.push({ name: 'account' })
    } catch {
      await router.push('/new-profile/account')
    }
    return
  }

  if (userId) {
    loading.value = true
    try {
      const response = await axios.post(`${api_base_url}/api/equipments/`, formData, {
        withCredentials: true,
      })
      if (response.status === 201) {
        const newEquipmentId = response.data.id
        router.push({ name: 'equipment-details', params: { id: newEquipmentId } })
        showNotification(
          'Item Listing Successful',
          `${response.data.name} created successfully!`,
          'success'
        )
        resetFormFields()
        clearFormCookies()
      } else {
        showNotification('Error Listing Item', 'Unexpected response status!', 'error')
      }
    } catch (error) {
      handleError(error)
    } finally {
      loading.value = false
    }
  } else {
    saveFormDataToCookies()
    authStore.redirectTo = '/list-item'
    router.push({ name: 'login' })
  }
}

// Confirm role switch and proceed with submission
const confirmRoleSwitch = async () => {
  showRoleConfirm.value = false
  
  try {
    // Switch role using auth store
    authStore.isOn = !authStore.isOn
    await authStore.updateUserRole()
    
    // After role switch, trigger form submission
    await handleSubmit()
  } catch (error) {
    logger.error('Role switch failed', error)
    showNotification('Role Switch Error', 'Unable to switch role. Please try again.', 'error')
  } finally {
    pendingRoleSwitch.value = false
  }
}

// Cancel role switch
const cancelRoleSwitch = () => {
  pendingRoleSwitch.value = false
  showRoleConfirm.value = false
}

const resetFormFields = () => {
  itemName.value = ''
  hourlyRate.value = null
  selectedCategory.value = null
  tags.value = []
  description.value = ''
  specifications.value = ''
  terms.value = ''
  streetAddress.value = ''
  city.value = ''
  state.value = ''
  zipCode.value = ''
  country.value = ''
  availableItems.value = null
  images.value = []
  imagePreviews.value = []
  if (descriptionQuill) descriptionQuill.setText('')
  if (termsQuill) termsQuill.setText('')
}

const handleError = (error) => {
  let title = 'Something went wrong'
  let message = 'An unexpected error occurred.'
  let type = 'error'

  if (error.response) {
    const { status, data } = error.response

    message =
      data?.detail ||
      data?.details ||
      data?.message ||
      data?.error ||
      error.response.statusText ||
      message

    if (status === 400 && typeof data === 'object') {
      const values = Object.values(data).flat()

      if (values.some((msg) => /please wait for verification/i.test(msg))) {
        type = 'info'
        title = 'Pending Verification'
        message = 'You already submitted this item. Please wait for it to be verified.'
      } else if (values.some((msg) => /already exists/i.test(msg))) {
        type = 'info'
        title = 'Duplicate Item'
        message = 'You already have an item with this name.'
      } else {
        const firstKey = Object.keys(data)[0]
        const firstError = data[firstKey]
        message = `${firstKey}: ${
          Array.isArray(firstError) ? firstError.join(', ') : firstError
        }`
      }
    } else if (status === 401 || status === 403) {
      message = 'You are not authorized to perform this action.'
    } else if (status === 404) {
      message = 'The requested resource was not found.'
    } else if (status >= 500) {
      message = 'A server error occurred. Please try again later.'
    }

    showNotification(title, message, type)
  } else if (error.request) {
    showNotification(
      'Network Error',
      'No response received. Check your internet connection.',
      'error'
    )
  } else {
    showNotification(
      'Unexpected Error',
      `Unexpected error: ${error.message || error}`,
      'error'
    )
  }

  logger.error('Error details', error)
}

// ── Multi-step wizard ──
const currentStep = ref(1)
const totalSteps = 5
const stepLabels = ['Basics', 'Details', 'Photos', 'Location', 'Specs']

const validateStep = (step) => {
  if (step === 1) {
    validateItemName()
    validateCategory()
    validateHourlyRate()
    validateAvailableItems()
    return !itemNameError.value && !categoryError.value && !hourlyRateError.value && !availableItemsError.value
  }
  if (step === 2) {
    validateDescription()
    validateTerms()
    return !descriptionError.value && !termsError.value
  }
  if (step === 3) {
    validateImages()
    return !imageError.value
  }
  if (step === 4) {
    validateStreetAddress()
    validateCity()
    validateState()
    validateZipCode()
    validateCountry()
    return !streetAddressError.value && !cityError.value && !stateError.value && !zipCodeError.value && !countryError.value
  }
  return true
}

const goNext = () => {
  if (validateStep(currentStep.value)) {
    currentStep.value++
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const goPrev = () => {
  if (currentStep.value > 1) {
    currentStep.value--
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<style scoped>
@media (max-width: 640px) {
  form {
    padding: 0.5rem;
  }
}

/* Quill editor theming */
.quill-editor-wrap :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid var(--color-input-border, #e5e7eb);
  background: var(--color-bg2, #f9fafb);
  border-radius: 0.5rem 0.5rem 0 0;
}

.quill-editor-wrap :deep(.ql-container) {
  border: none;
  min-height: 160px;
  font-size: 0.875rem;
}

.quill-editor-wrap :deep(.ql-editor) {
  min-height: 160px;
  color: var(--color-input-text, inherit);
}

.quill-editor-wrap :deep(.ql-editor.ql-blank::before) {
  color: var(--color-muted, #9ca3af);
  font-style: normal;
}

.quill-editor-wrap :deep(.ql-stroke) {
  stroke: var(--color-text2, #6b7280);
}

.quill-editor-wrap :deep(.ql-fill) {
  fill: var(--color-text2, #6b7280);
}

.quill-editor-wrap :deep(.ql-picker-label) {
  color: var(--color-text2, #6b7280);
}

.quill-editor-wrap :deep(.ql-picker-options) {
  background: var(--color-input, #fff);
  border-color: var(--color-input-border, #e5e7eb);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.quill-editor-wrap :deep(.ql-picker-item) {
  color: var(--color-text, #1c1c1c);
}

.quill-editor-wrap :deep(.ql-picker-item:hover) {
  color: var(--color-primary-600, #e68d00);
}

.quill-editor-wrap :deep(.ql-editor h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.5em 0;
}

.quill-editor-wrap :deep(.ql-editor h2) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0.4em 0;
}

.quill-editor-wrap :deep(.ql-editor h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.35em 0;
}

.quill-editor-wrap :deep(.ql-editor blockquote) {
  border-left: 3px solid var(--color-primary-600, #e68d00);
  padding-left: 0.75rem;
  color: var(--color-text2, #6b7280);
  margin: 0.5em 0;
}

.quill-editor-wrap :deep(.ql-editor a) {
  color: var(--color-primary-600, #e68d00);
  text-decoration: underline;
}

.quill-editor-wrap :deep(.ql-editor ol),
.quill-editor-wrap :deep(.ql-editor ul) {
  padding-left: 1.5em;
}

.quill-editor-wrap :deep(.ql-snow .ql-tooltip) {
  background: var(--color-input, #fff);
  border-color: var(--color-input-border, #e5e7eb);
  color: var(--color-text, #1c1c1c);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-radius: 0.5rem;
  z-index: 10;
}

.quill-editor-wrap :deep(.ql-snow .ql-tooltip input[type="text"]) {
  background: var(--color-bg2, #f9fafb);
  border-color: var(--color-input-border, #e5e7eb);
  color: var(--color-input-text, inherit);
  border-radius: 0.375rem;
}

.quill-editor-wrap :deep(.ql-snow .ql-tooltip a.ql-action::after),
.quill-editor-wrap :deep(.ql-snow .ql-tooltip a.ql-remove::before) {
  color: var(--color-primary-600, #e68d00);
}

/* Category select option styling */
select option {
  background: var(--color-inputBg, #fff);
  color: var(--color-inputText, #1c1c1c);
}

/* Native select dark mode fix */
select {
  color-scheme: light dark;
}
</style>
